import { Box, Button, CircularProgress } from '@mui/material';
import moment from 'moment';
import React, { useCallback, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useLoggedInEmployeeId } from '~/components/Auth';
import { SolaForceSubheading } from '~/components/SolaForceSubheading';
import {
  EmploymentDetailsProposalProps,
  EmploymentPositionProposalProps,
  ProposalStatus,
} from '~/data/proposals';
import { EmploymentProposalsDataAndConfig } from '~/features/ScheduledEmploymentChanges/Common/EmploymentProposals';
import { scheduledDateFormats } from '~/features/ScheduledEmploymentChanges/helpers';
import {
  EmploymentUpcomingChanges,
  UpcomingChangesWrapper,
} from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/Common';
import { FormComponent } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/ScheduleEmploymentDetail/components/FormComponent';
import { useUpcomingScheduledEmploymentDetail } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/ScheduleEmploymentDetail/useUpcomingScheduledEmploymentDetail';
import {
  ChangedStartDateState,
  Form,
  PendingScheduleChanges,
} from '~/features/ScheduledEmploymentChanges/ScheduleChanges/useScheduleChanges';

import { OngoingEmploymentDetailProposalDialog } from './components/OngoingEmploymentDetailProposalDialog';
import { useScheduleEmploymentDetail } from './useScheduleEmploymentDetail';
import { useSxStyles } from './useStyles';

type PropsType = {
  scheduleFormMethods: UseFormReturn<Form>;
  empId: number;
  index: number;
  employmentDetailPending?: PendingScheduleChanges[];
  setChangedStartDate: React.Dispatch<
    React.SetStateAction<ChangedStartDateState>
  >;
  resetFormAfterProposalDecision: ({
    emplDetPropData,
    empPositionPropData,
  }: {
    emplDetPropData?: EmploymentDetailsProposalProps;
    empPositionPropData?: EmploymentPositionProposalProps;
  }) => void;
  allResponsibleHRIds: number[];
  proposalsData: EmploymentProposalsDataAndConfig;
};

export const ScheduleEmploymentDetail = (props: PropsType) => {
  const {
    scheduleFormMethods,
    empId,
    employmentDetailPending,
    index,
    setChangedStartDate,
    allResponsibleHRIds,
    resetFormAfterProposalDecision,
    proposalsData,
  } = props;

  const sxStyles = useSxStyles();
  const { t } = useTranslation();
  const {
    loading,
    employmentDetail,
    employmentStatuses,
    jobGrades,
    getPersonnelGroupOptions,
    defaultData,
    upcomingChangesOpen,
    setUpComingChangesOpen,
    fComMaxPersonnelGroups,
    proposals,
    onProposalDecision,
    isHRUser,
    newEngine,
  } = useScheduleEmploymentDetail({
    empId,
    resetFormAfterProposalDecision,
    employmentDetailPending:
      employmentDetailPending?.[index]?.employmentDetails,
    proposalsData,
  });

  const currentEmplWF = newEngine && index === 0;
  const loggedInUserId = useLoggedInEmployeeId();

  const hrSelectorDisable =
    isHRUser && allResponsibleHRIds.includes(loggedInUserId);

  const [showProposal, setShowProposal] =
    useState<EmploymentDetailsProposalProps | null>(null);

  const { proposalChanges, upcomingChanges } =
    useUpcomingScheduledEmploymentDetail({
      proposals,
      proposalWorkflowsEnabled: currentEmplWF,
      defaultData,
      pendingScheduleChanges: {
        index,
        pendingData: employmentDetailPending,
      },
      fieldValueFormatProps: {
        jobGrades,
        employmentStatuses,
        getPersonnelGroupOptions,
      },
    });

  const UpcomingChangesComponent = useCallback(() => {
    if (
      !currentEmplWF &&
      !!employmentDetailPending?.[index]?.employmentDetails &&
      defaultData &&
      upcomingChanges.length
    ) {
      return (
        <UpcomingChangesWrapper
          upcomingChangesOpen={upcomingChangesOpen}
          setUpComingChangesOpen={setUpComingChangesOpen}
        >
          <EmploymentUpcomingChanges
            label={moment(
              employmentDetailPending?.[index]?.employmentDetails
                .fEmplDetailStartDate,
              scheduledDateFormats.dashFormat,
            ).format(scheduledDateFormats.dotFormat)}
            valueChanges={upcomingChanges}
          />
        </UpcomingChangesWrapper>
      );
    }
    if (currentEmplWF && proposalChanges.upcoming.length > 0) {
      return (
        <UpcomingChangesWrapper
          upcomingChangesOpen={upcomingChangesOpen}
          setUpComingChangesOpen={setUpComingChangesOpen}
        >
          {proposalChanges.upcoming.map((upcomingChanges, i) => {
            return (
              <EmploymentUpcomingChanges
                key={`upcoming-${i}`}
                {...upcomingChanges}
              />
            );
          })}
        </UpcomingChangesWrapper>
      );
    }
    return null;
  }, [
    currentEmplWF,
    defaultData,
    employmentDetailPending,
    index,
    setUpComingChangesOpen,
    upcomingChangesOpen,
    upcomingChanges,
    proposalChanges.upcoming,
  ]);

  const OngoingProposals = () => {
    if (!!proposalChanges.onGoing) {
      const { proposalId, label, valueChanges } = proposalChanges.onGoing;
      const onGoingProposal = proposals.find(
        (p) => p.proposalId === proposalId,
      );

      return (
        <>
          <EmploymentUpcomingChanges label={label} valueChanges={valueChanges}>
            {!!onGoingProposal && (
              <Button
                size="small"
                variant="contained"
                onClick={() => setShowProposal(onGoingProposal)}
                data-testid="employment-detail-workflow-status-button"
              >
                {t('seeWorkflowStatus')}
              </Button>
            )}
          </EmploymentUpcomingChanges>
          {!!showProposal && (
            <OngoingEmploymentDetailProposalDialog
              open
              proposal={showProposal}
              onClose={() => setShowProposal(null)}
              onSubmit={onProposalDecision}
            />
          )}
        </>
      );
    }

    return null;
  };

  return (
    <Box>
      <SolaForceSubheading>{`${t('laSchedule')} - ${t(
        'laEmplDetail',
      )}`}</SolaForceSubheading>
      {loading || !employmentDetail ? (
        <Box sx={sxStyles.loadingMiddle}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <FormComponent
            scheduleFormMethods={scheduleFormMethods}
            employmentStatuses={employmentStatuses}
            jobGrades={jobGrades}
            getPersonnelGroupOptions={getPersonnelGroupOptions}
            index={index}
            fComMaxPersonnelGroups={fComMaxPersonnelGroups}
            newEmpl={!employmentDetailPending?.[index] && index !== 0}
            setChangedStartDate={setChangedStartDate}
            allResponsibleHRIds={allResponsibleHRIds}
            hrSelectorDisable={hrSelectorDisable}
            pendingProposal={
              currentEmplWF &&
              proposals.length > 0 &&
              proposals[0].proposalStatus === ProposalStatus.IN_PROGRESS
            }
            inProgressPropStartDate={
              proposals.length > 0 && currentEmplWF
                ? proposals[0].proposalData.ed.start_date
                : undefined
            }
            newEngine={newEngine}
          />
          <OngoingProposals />
          <UpcomingChangesComponent />
        </>
      )}
    </Box>
  );
};
